import {announce} from '@react-aria/live-announcer'
import {toast} from 'sonner'

import {useAccountInformationValue} from '@/auth/hooks/useAccountInformation'
import {Addresses, type ShippingInfo} from '@/lib/payment/paymenModel'
import {putAddresses} from '@/lib/payment/paymentService'

import {getAddressesQueryKey} from './useAddresses'

type AddressesType = typeof Addresses.infer

export type AddressOperation =
  | {type: 'add'; addressData: ShippingInfo; makeDefault: boolean}
  | {type: 'edit'; addressData: ShippingInfo; makeDefault: boolean; editIndex: number}
  | {type: 'delete'; editIndex: number}
  | {type: 'setDefault'; editIndex: number}

export const updateAddressesByOperation = (
  currentAddresses: AddressesType,
  operation: AddressOperation,
): AddressesType => {
  const existingShippingInfos = currentAddresses.shippingInfos ?? []
  let updatedShippingInfos: ShippingInfo[]

  switch (operation.type) {
    case 'add': {
      updatedShippingInfos = [...existingShippingInfos, operation.addressData]
      if (operation.makeDefault) {
        updatedShippingInfos = [operation.addressData, ...existingShippingInfos]
      }
      break
    }
    case 'edit': {
      updatedShippingInfos = existingShippingInfos.map((info, idx) =>
        idx === operation.editIndex ? operation.addressData : info,
      )
      if (operation.makeDefault) {
        const editedAddress = updatedShippingInfos[operation.editIndex]
        if (!editedAddress) break

        updatedShippingInfos = [
          editedAddress,
          ...updatedShippingInfos.slice(0, operation.editIndex),
          ...updatedShippingInfos.slice(operation.editIndex + 1),
        ]
      }
      break
    }
    case 'delete': {
      updatedShippingInfos = [
        ...existingShippingInfos.slice(0, operation.editIndex),
        ...existingShippingInfos.slice(operation.editIndex + 1),
      ]
      break
    }
    case 'setDefault': {
      const selected = existingShippingInfos[operation.editIndex]
      if (!selected) return currentAddresses
      updatedShippingInfos = [
        selected,
        ...existingShippingInfos.slice(0, operation.editIndex),
        ...existingShippingInfos.slice(operation.editIndex + 1),
      ]
      break
    }
  }

  return {
    billingInfo: currentAddresses.billingInfo,
    shippingInfos: updatedShippingInfos,
  }
}

const getSuccessMessage = (operation: AddressOperation, addressName: string): string => {
  switch (operation.type) {
    case 'add':
      return operation.makeDefault
        ? `Success: New address for ${addressName} has been added and set as default.`
        : `Success: New address for ${addressName} has been added successfully.`
    case 'edit':
      return `Success: Address for ${addressName} has been edited and updated successfully.`
    case 'delete':
      return `Success: Address for ${addressName} has been removed successfully.`
    case 'setDefault':
      return `Success: Address for ${addressName} has been set as your default address.`
  }
}

export function useAddressOperations() {
  const accountInformation = useAccountInformationValue()

  const getValidatedAddresses = (queryClient: QueryClient): AddressesType => {
    const rawData = queryClient.getQueryData(getAddressesQueryKey(accountInformation?.id))

    if (rawData) {
      return Addresses(rawData)
    }

    return {billingInfo: undefined, shippingInfos: []}
  }

  const mutation = useMutation({
    mutationFn: async (operation: AddressOperation, context) => {
      const currentAddresses = getValidatedAddresses(context.client)

      const updatedAddresses = updateAddressesByOperation(currentAddresses, operation)

      return putAddresses({addresses: updatedAddresses})
    },

    onSuccess: (_data: unknown, operation: AddressOperation, result, context) => {
      const currentAddresses = getValidatedAddresses(context.client)

      const addressName =
        'addressData' in operation
          ? (operation.addressData.name?.trim() ?? 'Unnamed')
          : (currentAddresses.shippingInfos?.[operation.editIndex]?.name?.trim() ?? 'Unnamed')

      const message = getSuccessMessage(operation, addressName)

      announce(message)
      toast.success(message)

      void context.client.invalidateQueries({
        queryKey: getAddressesQueryKey(accountInformation?.id),
      })
    },

    onError: (err: Error, operation: AddressOperation) => {
      const message = `Error: ${err.message || 'Action has failed. Please try again.'}`
      console.error('Address operation failed:', {
        operation: operation.type,
        error: err.message,
        stack: err.stack,
      })
      toast.error(message)
    },
  })

  return {
    mutation,
    addAddress: (addressData: ShippingInfo, makeDefault: boolean = false) => {
      mutation.mutate({type: 'add', addressData, makeDefault})
    },

    editAddress: (editIndex: number, addressData: ShippingInfo, makeDefault: boolean = false) => {
      mutation.mutate({type: 'edit', addressData, makeDefault, editIndex})
    },

    deleteAddress: (editIndex: number) => {
      mutation.mutate({type: 'delete', editIndex})
    },

    setDefaultAddress: (editIndex: number) => {
      mutation.mutate({type: 'setDefault', editIndex})
    },
  }
}
